import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, BoxExtendedProps, Text, useResponsive } from '@common-fe/common-fe';

import { UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';

interface Props extends BoxExtendedProps {
  testId: string;
  type: UseMyMoneyOption;
  icon: React.ReactNode;
  title: string;
  description: string;
  onChoose: (option: UseMyMoneyOption) => void;
  isSelected?: boolean;
}

const UseMyMoneyOptionsListItem: React.FC<Props> = ({
  testId,
  type,
  icon,
  title,
  description,
  onChoose,
  isSelected,
  ...props
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const handleChoose = useCallback(() => {
    onChoose(type);
  }, [onChoose, type]);

  if (isMobile) {
    return (
      <Box
        data-testid={`use-my-money-option_${testId}`}
        round="container1Round"
        background={isSelected ? 'accentContainer' : 'border2'}
        pad="spacing12"
        {...props}
      >
        <Box direction="row" align="center">
          <Box data-testid={`use-my-money-option_${testId}_icon`}>
            {icon}
          </Box>
          <Text
            data-testid={`use-my-money-option_${testId}_title`}
            weight={700}
            size="20px"
            style={{ lineHeight: '28px' }}
            margin={{ horizontal: 'spacing8' }}
          >
            {title}
          </Text>
          {isSelected ? null : (
            <Box margin={{ left: 'auto' }}>
              <AppButton
                testId={`use-my-money-option_${testId}_choose`}
                onClick={handleChoose}
                style={{ minWidth: '77px', minHeight: '40px', height: '40px' }}
              >
                {t('Choose')}
              </AppButton>
            </Box>
          )}
        </Box>
        <Text data-testid={`use-my-money-option_${testId}_description`} color="textSecondary" margin={{ top: 'spacing8' }}>
          {description}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      data-testid={`use-my-money-option_${testId}`}
      direction="row"
      round="container1Round"
      background={isSelected ? 'accentContainer' : 'border2'}
      pad={{ vertical: 'spacing16', horizontal: 'spacing20' }}
      align="center"
      {...props}
    >
      <Box data-testid={`use-my-money-option_${testId}_icon`}>
        {icon}
      </Box>

      <Box data-testid={`use-my-money-option_${testId}_content`} margin={{ horizontal: 'spacing12' }} flex>
        <Text data-testid={`use-my-money-option_${testId}_title`} weight={700} size="20px" style={{ lineHeight: '28px' }}>
          {title}
        </Text>
        <Text data-testid={`use-my-money-option_${testId}_description`} color="textSecondary">
          {description}
        </Text>
      </Box>

      {isSelected ? null : (
        <Box margin={{ left: 'auto' }}>
          <AppButton
            testId={`use-my-money-option_${testId}_choose`}
            onClick={handleChoose}
            style={{ minWidth: '100px' }}
          >
            {t('Choose')}
          </AppButton>
        </Box>
      )}
    </Box>
  );
};

export default UseMyMoneyOptionsListItem;
